import styled from 'styled-components';
import { LegTableView } from '@paradigm/features/src/unified-rfqs/ui/types';
import { Side } from '@paradigm/features/src/unified-rfqs/entities/shared';
import {
  LegsTableContainer,
  Header,
  TableHeader,
  Row,
  ColoredAction,
  QuantityCol,
} from '#/components/rfq-common/LegsTable';
import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';
import { NEGATIVE_SIGN } from '@paradigm/utils/src/enhanceNegativeSign';
import { roundedLegRatio } from '@paradigm/features/src/unified-rfqs/utils/legs';
import PopoverCell from './PopoverCell';

interface LegTableProps {
  readonly legs: readonly LegTableView[];
  readonly side?: Side | null;
  readonly isLoading?: boolean;
  readonly showContractTerms?: boolean;
  readonly errorMessage?: string;
}

function LegsTable({
  legs,
  side = 'BUY',
  isLoading = false,
  errorMessage = '',
  showContractTerms = false,
}: LegTableProps) {
  const loader = (
    <div css="text-align: center; padding: 1rem;">
      <LoadingSpinner alt="loading..." />
    </div>
  );

  const tableBody =
    errorMessage !== '' ? (
      <div css="text-align: center; padding: 1rem;">
        <p>{errorMessage} 😓</p>
      </div>
    ) : (
      legs.map((leg) => (
        <LegRow
          showContractTerms={showContractTerms}
          key={leg.index}
          leg={leg}
        />
      ))
    );

  return (
    <LegsTableContainer data-testid="um-quote-modal-legs-table">
      <Header>Strategy Details{side === 'BUY' ? '(Buyer)' : '(Seller)'}</Header>
      <TableHeader>
        <RowStyled>
          <span>Leg</span>
          <span>Product</span>
          <span>Action</span>
          <span>Instrument</span>
          <span>Expiry</span>
          <span>Strike</span>
          <span>Leg Price</span>
          <span>Ratio</span>
          <span>Qty</span>
        </RowStyled>
      </TableHeader>
      {isLoading ? loader : tableBody}
    </LegsTableContainer>
  );
}

interface LegRowProps {
  readonly leg: LegTableView;
  readonly showContractTerms: boolean;
  readonly className?: string;
}

const makeRowId = (id: number) => `um-order-modal-leg-row-${id}`;

function LegRow({ leg, showContractTerms, className }: LegRowProps) {
  const rowId = makeRowId(leg.index);
  const { roundedRatio, isRounded } = roundedLegRatio(leg.ratio);

  return (
    <RowStyled
      className={className}
      data-testid={rowId}
      aria-label="strategy leg"
      role="row"
    >
      <span aria-label="row">{leg.rowNumber}</span>
      <span aria-label="product">{leg.productLabel}</span>
      <ColoredAction aria-label="action">{leg.side}</ColoredAction>
      <span aria-label="instrument">{leg.instrumentLabel}</span>
      <span aria-label="expiry">{leg.expiry}</span>
      <span aria-label="strike">{leg.strike}</span>
      <span aria-label="price">{leg.price}</span>
      <PopoverCell
        id={`${rowId}-ratio`}
        shouldDisplayPopover={isRounded}
        popoverValue={leg.ratio}
        ariaLabel="ratio"
      >
        {roundedRatio}
      </PopoverCell>
      <LegQuantity leg={leg} showContractTerms={showContractTerms} />
    </RowStyled>
  );
}

export function LegQuantity({
  leg,
  showContractTerms,
  className,
}: LegRowProps) {
  return showContractTerms ? (
    <QuantityCol className={className}>
      <div aria-label="clearing quantity">{leg.clearingUnitiesQuantity}</div>
      <div aria-label="contract quantity">{leg.contractTermsQuantity}</div>
    </QuantityCol>
  ) : (
    <QuantityCol className={className}>
      <div aria-label="quantity">{leg.quantity ?? NEGATIVE_SIGN}</div>
      <div css="opacity: 0.7; font-size: 10px;" aria-label="clearing quantity">
        {leg.clearingUnitiesQuantity}
      </div>
    </QuantityCol>
  );
}

const RowStyled = styled(Row)`
  grid-template-columns: 30px 80px 42px 70px 66px 50px 66px 50px 130px;
`;

export default LegsTable;
